import { useState } from 'react';
import Shell from '../components/Shell';
import Seo from '../components/Seo';
import { PageHero, Section } from '../components/Pieces';
import { contactInfo } from '../mock';
import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { api, apiError } from '../lib/api';

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', org: '', phone: '', interest: 'General enquiry', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast({ title: 'Please add your name and email.' }); return; }
    setLoading(true);
    try {
      await api.post('/contact', form);
      setForm({ name: '', email: '', org: '', phone: '', interest: 'General enquiry', message: '' });
      toast({ title: 'Message received', description: 'Thank you — our team will get back within two working days.' });
    } catch (err) {
      toast({ title: 'Could not send your message', description: apiError(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <Seo title="Contact"
        description="Reach Mashmari for pilots, partnerships, standards work, CSR programmes or a demonstration of any capability." />
      <PageHero
        kicker="Contact"
        title="Let’s design what happens in your classroom."
        sub="Reach us for pilots, partnerships, standards work, CSR programmes or a demo of any capability."
        breadcrumbs={[{ label: 'Contact' }]}
      />
      <Section bg="white">
        <div className="grid lg:grid-cols-[380px_1fr] gap-10">
          <aside className="space-y-6">
            <InfoTile icon={Mail} label="Email" value={contactInfo.email} href={`mailto:${contactInfo.email}`} />
            <InfoTile icon={Phone} label="Phone / WhatsApp" value={contactInfo.phone} href={`tel:${contactInfo.phone}`} />
            <InfoTile icon={Clock} label="Opening Hours" value={contactInfo.openingHours} />
            <div className="bg-[var(--mm-cream)] border border-[var(--mm-line)] rounded-2xl p-6">
              <div className="mm-kicker mb-3">Offices</div>
              <ul className="space-y-4 text-sm text-[var(--mm-ink)]/80">
                {contactInfo.workAddresses.map((a, i) => (
                  <li key={i} className="flex gap-3"><MapPin className="w-4 h-4 text-[var(--mm-purple)] mt-0.5 shrink-0"/><span>{a}</span></li>
                ))}
                <li className="flex gap-3"><MapPin className="w-4 h-4 text-[var(--mm-purple)] mt-0.5 shrink-0"/><span>{contactInfo.registeredAddress}</span></li>
              </ul>
            </div>
          </aside>

          <form onSubmit={handleSubmit} className="bg-[var(--mm-cream)] border border-[var(--mm-line)] rounded-2xl p-6 md:p-8 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Name" name="name" value={form.name} onChange={handleChange} required />
              <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Organisation" name="org" value={form.org} onChange={handleChange} />
              <Field label="Phone" name="phone" value={form.phone} onChange={handleChange} />
            </div>
            <div>
              <label className="block text-xs font-semibold tracking-wider text-[var(--mm-ink)]/70 uppercase mb-2">Interested in</label>
              <select name="interest" value={form.interest} onChange={handleChange} data-testid="contact-interest-select" className="w-full h-12 px-4 rounded-xl bg-white border border-[var(--mm-line)] text-[var(--mm-ink)]">
                {['General enquiry','School Transformation','Digital TLM Engine','Smart ECCE / Digital Anganwadi','Village School Transformation','Remote & Autonomous Classrooms','NetRakshak','Standards / Research','CSR Partnership'].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold tracking-wider text-[var(--mm-ink)]/70 uppercase mb-2">Message</label>
              <textarea name="message" rows={6} value={form.message} onChange={handleChange} data-testid="contact-message-input" placeholder="Tell us a little about your context and what you’d like to explore…" className="w-full p-4 rounded-xl bg-white border border-[var(--mm-line)] text-[var(--mm-ink)] resize-none placeholder:text-[var(--mm-ink)]/40" />
            </div>
            <div className="flex justify-end pt-2">
              <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="btn-primary disabled:opacity-60">{loading ? 'Sending…' : 'Send message'}<Send className="w-4 h-4"/></button>
            </div>
          </form>
        </div>
      </Section>
    </Shell>
  );
}

function Field({ label, name, type='text', value, onChange, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold tracking-wider text-[var(--mm-ink)]/70 uppercase mb-2">{label}{required && <span className="text-[var(--mm-purple)] ml-1">*</span>}</label>
      <input type={type} name={name} value={value} onChange={onChange} required={required} data-testid={`contact-${name}-input`} className="w-full h-12 px-4 rounded-xl bg-white border border-[var(--mm-line)] text-[var(--mm-ink)]" />
    </div>
  );
}

function InfoTile({ icon: Icon, label, value, href }) {
  const Wrap = href ? 'a' : 'div';
  return (
    <Wrap href={href} className="flex items-start gap-4 bg-white border border-[var(--mm-line)] rounded-2xl p-5 hover:border-[var(--mm-purple)]/50 transition-colors block">
      <div className="w-11 h-11 rounded-full bg-[var(--mm-purple)] text-white flex items-center justify-center shrink-0"><Icon className="w-4 h-4"/></div>
      <div>
        <div className="mm-kicker">{label}</div>
        <div className="mt-1 font-semibold text-[var(--mm-ink)]">{value}</div>
      </div>
    </Wrap>
  );
}
